import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clientes from "./pages/Clientes";
import Colaboradores from "./pages/Colaboradores";
import Servicos from "./pages/Servicos";
import Agendamento from "./pages/Agendamento";
import Financeiro from "./pages/Financeiro";
import Orcamentos from "./pages/Orcamentos";
import Pix from "./pages/Pix";
import Usuarios from "./pages/Usuarios";
import PublicOrcamento from "./pages/PublicOrcamento";
import PublicPix from "./pages/PublicPix";
import PublicLead from "./pages/PublicLead";
import Leads from "./pages/Leads";
import GradeOperacional from "./pages/GradeOperacional";
import CidadesAtendimento from "./pages/CidadesAtendimento";
import Parceiros from "./pages/Parceiros";
import ParceiroPerfilAdmin from "./pages/ParceiroPerfilAdmin";
import SejaParceiro from "./pages/SejaParceiro";
import ParceiroApp from "./pages/parceiro/ParceiroApp";
import ColabPortal from "./pages/ColabPortal";
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children, roles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/sistema" replace />;
  }

  return children;
};

// Public Route Component (redirects to sistema if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/sistema" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Orçamento Form */}
      <Route path="/orcamento" element={<PublicOrcamento />} />
      <Route path="/orçamento" element={<PublicOrcamento />} />
      
      {/* Public Lead Capture Form */}
      <Route path="/lead" element={<PublicLead />} />
      <Route path="/contato" element={<PublicLead />} />
      
      {/* Public PIX Payment */}
      <Route path="/pagar/:id" element={<PublicPix />} />

      {/* Public Seja Parceiro */}
      <Route path="/sejaparceiro" element={<SejaParceiro />} />
      <Route path="/seja-parceiro" element={<Navigate to="/sejaparceiro" replace />} />
      
      {/* Portal do Parceiro */}
      <Route path="/parceiro/*" element={<ParceiroApp />} />
      <Route path="/colaborador" element={<ColabPortal />} />

      {/* Login Page */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      
      {/* Protected System Routes */}
      <Route
        path="/sistema"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/clientes"
        element={
          <ProtectedRoute roles={['admin', 'atendente']}>
            <Layout>
              <Clientes />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/colaboradores"
        element={
          <ProtectedRoute roles={['admin']}>
            <Layout>
              <Colaboradores />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/servicos"
        element={
          <ProtectedRoute roles={['admin', 'atendente']}>
            <Layout>
              <Servicos />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/agendamento"
        element={
          <ProtectedRoute roles={['admin', 'atendente']}>
            <Layout>
              <Agendamento />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/grade"
        element={
          <ProtectedRoute roles={['admin', 'atendente']}>
            <Layout>
              <GradeOperacional />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/financeiro"
        element={
          <ProtectedRoute roles={['admin', 'financeiro']}>
            <Layout>
              <Financeiro />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/orcamentos"
        element={
          <ProtectedRoute>
            <Layout>
              <Orcamentos />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/leads"
        element={
          <ProtectedRoute roles={['admin', 'atendente']}>
            <Layout>
              <Leads />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/cidades-atendimento"
        element={
          <ProtectedRoute roles={['admin']}>
            <Layout>
              <CidadesAtendimento />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/parceiros"
        element={
          <ProtectedRoute roles={['admin']}>
            <Layout>
              <Parceiros />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/parceiros/:id"
        element={
          <ProtectedRoute roles={['admin']}>
            <Layout>
              <ParceiroPerfilAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/pix"
        element={
          <ProtectedRoute roles={['admin', 'financeiro']}>
            <Layout>
              <Pix />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sistema/usuarios"
        element={
          <ProtectedRoute roles={['admin']}>
            <Layout>
              <Usuarios />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Redirect old routes to new ones */}
      <Route path="/clientes" element={<Navigate to="/sistema/clientes" replace />} />
      <Route path="/colaboradores" element={<Navigate to="/sistema/colaboradores" replace />} />
      <Route path="/servicos" element={<Navigate to="/sistema/servicos" replace />} />
      <Route path="/agendamento" element={<Navigate to="/sistema/agendamento" replace />} />
      <Route path="/financeiro" element={<Navigate to="/sistema/financeiro" replace />} />
      <Route path="/orcamentos" element={<Navigate to="/sistema/orcamentos" replace />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
